import { StaticVariables } from "../../../Utils/StaticVariables";

export const VIBE_RULES = [
  "You generate ONE self-contained HTML fragment for a section of a creator's website.",
  "Rules:",
  "- Output ONLY the HTML fragment. No markdown fences, no commentary, no <html>/<head>/<body>.",
  '- Use inline style="" attributes only. No style blocks, no classes, no external CSS.',
  "- No scripts, no event handlers, no iframes.",
  "- Images must use the hosted URLs provided. Do not invent external image URLs.",
  "- Keep it responsive: use %, flex/grid with flex-wrap, and max-width:100%.",
  "- Fidelity: implement ONLY what the creator asked for. Do not add extra headlines, subcopy, CTAs, buttons, cards, icons, stats, testimonials, or decorative blocks they did not request.",
  "- Do not invent placeholder copy (lorem, “Card 1”, sample bios, fake names, filler paragraphs). If they did not supply text for a slot, leave it empty or omit that element.",
  "- Prefer the smallest HTML that satisfies the request. A simple request stays a simple layout — do not expand it into a marketing page.",
  "- If prior HTML is provided and the request is a revision, change only what they asked to change; keep everything else.",
].join("\n");

export function stripUnsafeHtml(html: string): string {
  return String(html || "")
    .replace(/```[a-z]*\n?/gi, "")
    .replace(/```/g, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, "")
    .trim();
}

export const VIBE_MAX_FILES = 5;
export const VIBE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

export function formatVibeBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export interface VibeFileRec {
  name: string;
  size: number;
  type: string;
  thumb: string | null;
  file: File;
}

export function validateVibeFiles(
  picked: File[],
  existingCount = 0,
): { accepted: File[]; error: string | null } {
  const errors: string[] = [];
  const room = VIBE_MAX_FILES - existingCount;
  if (picked.length > room)
    errors.push(`Only ${VIBE_MAX_FILES} files allowed — kept the first ${Math.max(room, 0)}.`);
  const accepted = picked.slice(0, Math.max(room, 0)).filter((f) => {
    if (f.size > VIBE_MAX_BYTES) {
      errors.push(`${f.name} is over 10 MB.`);
      return false;
    }
    return true;
  });
  return { accepted, error: errors.length ? errors.join(" ") : null };
}

export type VibeChatMessage = {
  role: "user" | "assistant";
  text: string;
  files?: { name: string; thumb?: string }[];
};

/** Session-only chat — never written to template tweaks. */
const vibeSession = new Map<string, VibeChatMessage[]>();

export function vibeSessionKey(pageKey: string, sectionId: string): string {
  return `${pageKey}/${sectionId}`;
}

export function getVibeSessionChat(key: string): VibeChatMessage[] {
  return vibeSession.get(key) || [];
}

export function setVibeSessionChat(key: string, chat: VibeChatMessage[]): void {
  vibeSession.set(key, chat);
}

export function clearVibeSessionChat(key: string): void {
  vibeSession.delete(key);
}

export type VibeGenerateInput = {
  prompt: string;
  files?: { name: string; type: string; dataUrl?: string; url?: string }[];
  imageUrls?: string[];
  username?: string;
  userId?: string;
  priorHtml?: string;
  signal?: AbortSignal;
};

export type VibeGenerateResult = {
  ok: boolean;
  html?: string;
  images?: string[];
  error?: string;
};

function escapeHtml(s: string): string {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function postJson(
  url: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    data = {};
  }
  return { ok: res.ok, data };
}

function extractHtml(data: Record<string, unknown>): string {
  const raw = String(
    data.html || data.output || data.text || data.body || data.suggestion || "",
  );
  return stripUnsafeHtml(raw);
}

function looksLikeHtml(s: string): boolean {
  return /<\w+[\s>]/.test(s);
}

/** Minimal fallback markup — only the text/images provided, nothing invented. */
function wrapPlainAsSection(title: string, body: string, images: string[]): string {
  const imgBlocks = images
    .map(
      (u) =>
        `<div style="width:100%;max-width:420px;aspect-ratio:16/9;margin:0 auto 12px;border-radius:10px;background-image:url(${escapeHtml(u)});background-size:cover;background-position:center;"></div>`,
    )
    .join("");
  const titleBlock = String(title || "").trim()
    ? `<div style="font-size:18px;font-weight:700;line-height:1.35;margin-bottom:10px;">${escapeHtml(title.trim())}</div>`
    : "";
  const paras = String(body || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(
      (p) =>
        `<p style="margin:0 0 10px;font-size:14px;line-height:1.6;color:inherit;opacity:.85;">${escapeHtml(p)}</p>`,
    )
    .join("");
  return `<div style="text-align:center;padding:8px 4px;">
    ${imgBlocks}
    ${titleBlock}
    ${paras}
  </div>`;
}

function parseCardCount(prompt: string): number {
  const n = prompt.match(/(\d+)\s*[- ]?(up|card|column|col)/i);
  if (n) return Math.min(4, Math.max(2, Number(n[1])));
  if (/card row|cards|grid/i.test(prompt)) return 3;
  return 0;
}

function parseTargetDate(prompt: string): Date | null {
  const iso = prompt.match(/\b(20\d{2}-\d{1,2}-\d{1,2})\b/);
  if (iso) {
    const d = new Date(iso[1]);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const md = prompt.match(
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(20\d{2}))?/i,
  );
  if (md) {
    const months: Record<string, number> = {
      jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
      may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8,
      september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
    };
    const month = months[md[1].toLowerCase()];
    const day = Number(md[2]);
    const year = md[3] ? Number(md[3]) : new Date().getFullYear();
    const d = new Date(year, month, day, 12, 0, 0);
    if (d.getTime() < Date.now()) d.setFullYear(year + 1);
    return d;
  }
  return null;
}

/** Deterministic HTML when the model server is unavailable — no invented extras. */
export function composeLocalSection(input: VibeGenerateInput): string {
  const prompt = String(input.prompt || "").trim();
  const images = [
    ...(input.imageUrls || []),
    ...(input.files || []).map((f) => f.url).filter(Boolean) as string[],
  ].filter(Boolean);
  const lower = prompt.toLowerCase();

  if (/countdown|days until/i.test(lower)) {
    const target = parseTargetDate(prompt);
    const remain = target
      ? Math.max(0, Math.ceil((target.getTime() - Date.now()) / 86400000))
      : null;
    const dateLine = target
      ? `<div style="font-size:12px;opacity:.6;margin-top:12px;">${escapeHtml(
          target.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        )}</div>`
      : "";
    const daysBlock =
      remain != null
        ? `<div style="display:flex;justify-content:center;gap:10px;flex-wrap:wrap;">
        <div style="min-width:72px;border:1.5px solid currentColor;border-radius:10px;padding:12px 10px;">
          <div style="font-size:28px;font-weight:700;line-height:1;">${remain}</div>
          <div style="font-size:10px;letter-spacing:.1em;text-transform:uppercase;opacity:.6;margin-top:4px;">Days</div>
        </div>
      </div>`
        : `<p style="margin:0;font-size:14px;opacity:.75;">Add a date to show days remaining.</p>`;
    return `<div style="text-align:center;padding:10px 6px;">
      ${daysBlock}
      ${dateLine}
    </div>`;
  }

  const cards = parseCardCount(lower);
  if (cards) {
    const cells = Array.from({ length: cards }, (_, i) => {
      const img = images[i]
        ? `<div style="width:100%;aspect-ratio:4/3;border-radius:8px;background-image:url(${escapeHtml(images[i])});background-size:cover;background-position:center;"></div>`
        : `<div style="width:100%;aspect-ratio:4/3;border-radius:8px;background:currentColor;opacity:.08;"></div>`;
      return `<div style="flex:1;min-width:140px;border:1.5px solid currentColor;border-radius:12px;padding:12px;">${img}</div>`;
    }).join("");
    return `<div style="padding:4px 0;">
      <div style="display:flex;flex-wrap:wrap;gap:12px;">${cells}</div>
    </div>`;
  }

  // Use the request as body copy only — no fake eyebrow / marketing chrome.
  return wrapPlainAsSection("", prompt, images);
}

/**
 * Custom Section generation. Chat is session-only; persist `html` only.
 * Tries the vibe endpoint, then blog generate, then a local compositor.
 */
export async function generateCustomSection(
  input: VibeGenerateInput,
): Promise<VibeGenerateResult> {
  const prompt = String(input.prompt || "").trim();
  const files = input.files || [];
  const imageUrls = (input.imageUrls || []).filter(Boolean);
  if (!prompt && !files.length && !imageUrls.length) {
    return { ok: false, error: "Describe what you want first." };
  }

  const API_BASE = String(StaticVariables().API_BASE || "").replace(/\/+$/, "");
  const creatorRequest = prompt || "Build the section from the attached files.";
  const hasAttachedImages =
    imageUrls.length > 0 ||
    files.some((f) => Boolean(f.url || f.dataUrl) || /^image\//i.test(f.type));
  const payload = {
    prompt: `${VIBE_RULES}\n\nCreator request:\n${creatorRequest}`,
    files,
    images: imageUrls,
    username: input.username || "",
    userId: input.userId || "",
    priorHtml: input.priorHtml || "",
    // Never invent stock imagery — only use what the creator attached.
    generateImages: false,
    useAttachedImagesOnly: hasAttachedImages,
  };

  try {
    const primary = await postJson(
      `${API_BASE}/api/vibe/generate`,
      payload,
      input.signal,
    );
    if (primary.ok) {
      const html = extractHtml(primary.data);
      const images = Array.isArray(primary.data.images)
        ? primary.data.images.map((u) => String(u)).filter(Boolean)
        : [];
      if (html && looksLikeHtml(html)) return { ok: true, html, images };
    }
  } catch {
    if (input.signal?.aborted) return { ok: false, error: "" };
  }

  // Skip blog-generate fallback — it invents titles/body beyond the request.
  return { ok: true, html: composeLocalSection({ ...input, prompt: creatorRequest }) };
}
